import { useNavigate, useLocation } from 'react-router-dom';
import { localizePath, stripLang } from '../i18n/langs';
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  flagUrl: string;
}

const languages: Language[] = [
  { 
    code: 'es', 
    name: 'Español', 
    flagUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/70/Flag_of_Spain_%28civil%29.svg/1280px-Flag_of_Spain_%28civil%29.svg.png'
  },
  { 
    code: 'ca', 
    name: 'Català', 
    flagUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ce/Flag_of_Catalonia.svg'
  },
  { 
    code: 'en', 
    name: 'English', 
    flagUrl: 'https://flagcdn.com/gb.svg'
  },
  { 
    code: 'fr', 
    name: 'Français', 
    flagUrl: 'https://flagcdn.com/fr.svg'
  }
];

const LanguageSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageSelect = (lang: Language) => {
    // Cambia de idioma navegando a la URL equivalente (/, /ca, /en, /fr)
    navigate(localizePath(stripLang(location.pathname), lang.code) + location.search + location.hash);
    i18n.changeLanguage(lang.code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200 border border-white/20"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-white/20">
          <img 
            src={selectedLang.flagUrl} 
            alt={selectedLang.name}
            className="w-6 h-6 object-cover"
          />
        </div>
        <ChevronDown className={`w-4 h-4 text-white transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay to capture clicks outside */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 z-[90]"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Dropdown menu */}
          <div 
            className="absolute right-0 mt-2 py-2 w-48 bg-black/90 backdrop-blur-md rounded-lg shadow-lg ring-1 ring-white/20 transform transition-all duration-200 origin-top-right z-[100]"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="language-menu"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang)}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/10 transition-colors duration-200
                  ${selectedLang.code === lang.code ? 'bg-white/10 font-medium' : ''}
                `}
                role="menuitem"
              >
                <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/20">
                  <img 
                    src={lang.flagUrl} 
                    alt={lang.name}
                    className="w-6 h-6 object-cover"
                  />
                </div>
                <span className="text-white">{lang.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;